import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { KRA, F360, NB } from "../constants/testIds4";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Star, Target, ChartBar, Users, Trash } from "@phosphor-icons/react";

const LABELS_9BOX = {
  "3-3": { title: "Star", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  "3-2": { title: "High performer", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  "3-1": { title: "Solid performer", color: "bg-blue-50 text-blue-800 border-blue-200" },
  "2-3": { title: "Rising star", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  "2-2": { title: "Core player", color: "bg-amber-50 text-amber-800 border-amber-200" },
  "2-1": { title: "Effective", color: "bg-blue-50 text-blue-800 border-blue-200" },
  "1-3": { title: "Enigma", color: "bg-amber-50 text-amber-800 border-amber-200" },
  "1-2": { title: "Inconsistent", color: "bg-orange-50 text-orange-800 border-orange-200" },
  "1-1": { title: "Risk", color: "bg-red-50 text-red-800 border-red-200" },
};

const NUM_LABEL = { 1: "Low", 2: "Mid", 3: "High" };

export default function PerformanceAdvanced() {
  const { user, tenantId } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const isAdmin = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);

  const params = useMemo(() => (user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {}), [user?.role, tenantId]);

  useEffect(() => {
    api.get("/performance/cycles", { params }).then(({ data }) => {
      setCycles(data);
      if (data.length && !cycleId) setCycleId(data[0].id);
    });
    // eslint-disable-next-line
  }, [tenantId]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Talent · Performance 2.0</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">KRA · 360 · 9-Box</h1>
          <p className="text-sm text-zinc-500 mt-2">Structured KRAs with weighted rollups, multi-rater 360° feedback and 9-box talent grid.</p>
        </div>
        {cycles.length > 0 && (
          <div>
            <Label className="text-xs">Review cycle</Label>
            <Select value={cycleId} onValueChange={setCycleId}>
              <SelectTrigger className="w-[220px] rounded-sm" data-testid={KRA.cycleSelect}><SelectValue /></SelectTrigger>
              <SelectContent>{cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!cycleId ? (
        <div className="border border-zinc-200 bg-white p-8 rounded-sm text-center">
          <div className="text-sm text-zinc-500">No review cycles exist for this tenant yet.</div>
          <a href="/app/performance" className="inline-block mt-3 text-emerald-800 border border-emerald-300 hover:bg-emerald-50 rounded-sm px-4 py-2 text-sm font-semibold" data-testid="perf-adv-goto-cycles">Create a cycle →</a>
        </div>
      ) : (
        <Tabs defaultValue="kra">
          <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
            <TabsTrigger value="kra" className="rounded-sm"><Target size={14} className="mr-1" /> KRA / KPI</TabsTrigger>
            <TabsTrigger value="360" className="rounded-sm"><Users size={14} className="mr-1" /> 360° Feedback</TabsTrigger>
            <TabsTrigger value="9box" className="rounded-sm"><ChartBar size={14} className="mr-1" /> 9-Box Grid</TabsTrigger>
          </TabsList>

          <TabsContent value="kra" className="mt-4">
            <KRAPanel cycleId={cycleId} isAdmin={isAdmin} params={params} user={user} />
          </TabsContent>
          <TabsContent value="360" className="mt-4">
            <F360Panel cycleId={cycleId} isAdmin={isAdmin} params={params} user={user} />
          </TabsContent>
          <TabsContent value="9box" className="mt-4">
            <NineBoxPanel cycleId={cycleId} isAdmin={isAdmin} params={params} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ---------------- KRA ----------------
function KRAPanel({ cycleId, isAdmin, params, user }) {
  const [rows, setRows] = useState([]);
  const [mine, setMine] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", area: "", description: "", weight_percent: 25, kpis: [{ label: "", metric: "", target: "", weight_percent: 25 }] });
  const [rollup, setRollup] = useState({});

  const load = () => {
    if (isAdmin) {
      api.get("/kras", { params: { ...params, cycle_id: cycleId } }).then(({ data }) => setRows(data));
      api.get("/employees", { params }).then(({ data }) => setEmployees(data));
    }
    if (user?.employee_id) api.get("/kras/mine", { params: { cycle_id: cycleId } }).then(({ data }) => setMine(data));
  };
  useEffect(() => { if (cycleId) load(); /* eslint-disable-next-line */ }, [cycleId]);

  const create = async () => {
    try {
      const payload = { ...form, cycle_id: cycleId, weight_percent: Number(form.weight_percent), kpis: form.kpis.filter((k) => k.label).map((k) => ({ ...k, weight_percent: Number(k.weight_percent || 0) })) };
      if (params.tenant_id) payload.tenant_id = params.tenant_id;
      await api.post("/kras", payload);
      toast.success("KRA added");
      setOpen(false);
      setForm({ employee_id: "", area: "", description: "", weight_percent: 25, kpis: [{ label: "", metric: "", target: "", weight_percent: 25 }] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rate = async (id, patch) => {
    try { await api.patch(`/kras/${id}`, patch); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const showRollup = async (eid) => {
    try {
      const { data } = await api.get(`/kras/${eid}/rollup`, { params: { cycle_id: cycleId } });
      setRollup({ ...rollup, [eid]: data });
      toast.success(`Weighted rollup: ${data.rollup_rating ?? "—"} / 5`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const list = isAdmin ? rows : mine;
  const grouped = useMemo(() => {
    const g = {};
    for (const r of list) {
      const k = r.employee_id;
      (g[k] = g[k] || { name: r.employee_name || "Me", code: r.employee_code || "", items: [] }).items.push(r);
    }
    return g;
  }, [list]);

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={KRA.addBtn}><Plus size={16} className="mr-1.5" /> Add KRA</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">Add KRA</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="kra-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Key Result Area *</Label><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Delivery excellence" className="rounded-sm" data-testid="kra-area-input" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Weight %</Label><Input type="number" value={form.weight_percent} onChange={(e) => setForm({ ...form, weight_percent: e.target.value })} className="rounded-sm" /></div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">KPIs (measurable)</Label>
                    <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => setForm({ ...form, kpis: [...form.kpis, { label: "", metric: "", target: "", weight_percent: 25 }] })}><Plus size={12} className="mr-1" /> KPI</Button>
                  </div>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {form.kpis.map((k, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-end border border-zinc-200 rounded-sm p-2">
                        <div className="col-span-4"><Label className="text-[10px]">Label</Label><Input value={k.label} onChange={(e) => setForm({ ...form, kpis: form.kpis.map((x, y) => y === i ? { ...x, label: e.target.value } : x) })} className="rounded-sm h-8 text-xs" /></div>
                        <div className="col-span-3"><Label className="text-[10px]">Metric</Label><Input value={k.metric} onChange={(e) => setForm({ ...form, kpis: form.kpis.map((x, y) => y === i ? { ...x, metric: e.target.value } : x) })} className="rounded-sm h-8 text-xs" /></div>
                        <div className="col-span-3"><Label className="text-[10px]">Target</Label><Input value={k.target} onChange={(e) => setForm({ ...form, kpis: form.kpis.map((x, y) => y === i ? { ...x, target: e.target.value } : x) })} className="rounded-sm h-8 text-xs" /></div>
                        <div className="col-span-1"><Label className="text-[10px]">Wt%</Label><Input type="number" value={k.weight_percent} onChange={(e) => setForm({ ...form, kpis: form.kpis.map((x, y) => y === i ? { ...x, weight_percent: e.target.value } : x) })} className="rounded-sm h-8 text-xs font-mono-plex" /></div>
                        <div className="col-span-1 pb-0.5">{form.kpis.length > 1 && <button onClick={() => setForm({ ...form, kpis: form.kpis.filter((_, y) => y !== i) })} className="text-red-600 hover:opacity-70"><Trash size={14} /></button>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={create} className="btn-zenith rounded-sm" data-testid={KRA.saveBtn}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="space-y-4" data-testid={KRA.list}>
        {Object.keys(grouped).length === 0 && <div className="border border-zinc-200 bg-white p-8 rounded-sm text-center text-sm text-zinc-500">No KRAs for this cycle yet.</div>}
        {Object.entries(grouped).map(([eid, group]) => (
          <div key={eid} className="border border-zinc-200 bg-white rounded-sm">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100 bg-zinc-50">
              <div>
                <div className="font-display text-lg font-bold">{group.name}</div>
                <div className="text-xs font-mono-plex text-zinc-500">{group.code}</div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-3">
                  {rollup[eid] && <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Rollup {rollup[eid].rollup_rating ?? "—"} / 5</Badge>}
                  <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => showRollup(eid)} data-testid={KRA.rollupBtn(eid)}>Compute rollup</Button>
                </div>
              )}
            </div>
            <div className="divide-y divide-zinc-100">
              {group.items.map((k) => (
                <div key={k.id} className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-display text-base font-bold">{k.area}</div>
                      {k.description && <div className="text-sm text-zinc-600 mt-0.5">{k.description}</div>}
                      {(k.kpis || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {k.kpis.map((kp, i) => (
                            <Badge key={i} variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200 text-xs">{kp.label} · {kp.target}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300">Wt {k.weight_percent}%</Badge>
                  </div>
                  <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <RatingBlock label="Self" value={k.self_rating} editable={user?.employee_id === k.employee_id} onChange={(v) => rate(k.id, { self_rating: v })} testid={KRA.selfRating(k.id)} />
                    <RatingBlock label="Manager" value={k.manager_rating} editable={isAdmin} onChange={(v) => rate(k.id, { manager_rating: v })} testid={KRA.managerRating(k.id)} />
                    <RatingBlock label="Final (calibrated)" value={k.final_rating} editable={isAdmin} onChange={(v) => rate(k.id, { final_rating: v })} testid={KRA.finalRating(k.id)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RatingBlock({ label, value, editable, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 flex items-center gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} disabled={!editable} onClick={() => editable && onChange(n)} className={`p-1 transition-colors duration-150 ${editable ? "hover:opacity-70" : "opacity-90"}`}>
            <Star size={18} weight={value && n <= value ? "fill" : "regular"} className={value && n <= value ? "text-emerald-700" : "text-zinc-300"} />
          </button>
        ))}
        {value != null && <span className="text-xs text-zinc-500 ml-1">{value}/5</span>}
      </div>
    </div>
  );
}

// ---------------- 360 Feedback ----------------
function F360Panel({ cycleId, isAdmin, params, user }) {
  const [employees, setEmployees] = useState([]);
  const [mine, setMine] = useState({ as_reviewer: [], as_subject: [] });
  const [open, setOpen] = useState(false);
  const [subj, setSubj] = useState("");
  const [reviewers, setReviewers] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [subjectFeedback, setSubjectFeedback] = useState([]);
  const [subjectView, setSubjectView] = useState("");
  const [submitOpen, setSubmitOpen] = useState(null); // fid to submit
  const [f, setF] = useState({ strengths: "", improvements: "", rating_overall: 4, ratings_dimensions: { leadership: 4, delivery: 4, communication: 4 } });

  const load = () => {
    if (isAdmin) api.get("/employees", { params }).then(({ data }) => setEmployees(data));
    if (user?.employee_id) api.get("/feedback360/mine").then(({ data }) => setMine(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cycleId]);

  // When subject changes, fetch smart reviewer suggestions grouped by relation
  useEffect(() => {
    if (!subj) { setEligible([]); return; }
    setSuggestBusy(true);
    api.get(`/feedback360/eligible-reviewers/${subj}`)
      .then(({ data }) => setEligible(data || []))
      .catch(() => setEligible([]))
      .finally(() => setSuggestBusy(false));
  }, [subj]);

  const request = async () => {
    try {
      const payload = { cycle_id: cycleId, subject_employee_id: subj, reviewer_employee_ids: reviewers };
      if (params.tenant_id) payload.tenant_id = params.tenant_id;
      const { data } = await api.post("/feedback360/requests", payload);
      toast.success(`Requested ${data.created} feedbacks`);
      setOpen(false); setSubj(""); setReviewers([]); setEligible([]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    try {
      await api.post(`/feedback360/${submitOpen}/submit`, f);
      toast.success("Feedback submitted");
      setSubmitOpen(null);
      setF({ strengths: "", improvements: "", rating_overall: 4, ratings_dimensions: { leadership: 4, delivery: 4, communication: 4 } });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewSubject = async (eid) => {
    setSubjectView(eid);
    try {
      const { data } = await api.get(`/feedback360/subject/${eid}`, { params: { cycle_id: cycleId } });
      setSubjectFeedback(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleReviewer = (id) => setReviewers(reviewers.includes(id) ? reviewers.filter((x) => x !== id) : [...reviewers, id]);

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={F360.requestBtn}><Plus size={16} className="mr-1.5" /> Request 360</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">Request 360° feedback</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label className="text-xs">Subject employee *</Label>
                  <Select value={subj} onValueChange={setSubj}>
                    <SelectTrigger className="rounded-sm" data-testid="f360-subject-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                {/* Smart reviewer suggestions via /feedback360/eligible-reviewers */}
                {subj && (
                  <div className="border border-zenith-teal/30 bg-zenith-teal/[0.04] rounded-sm p-3" data-testid="f360-suggestions">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <div className="text-xs font-semibold text-adb-navy">
                        {suggestBusy ? "Finding reviewers…" : `Suggested reviewers (${eligible.length})`}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { key: "dotted_manager", label: "+ Dotted-line" },
                          { key: "reporting_manager", label: "+ Solid-line" },
                          { key: "direct_report", label: "+ Direct reports" },
                          { key: "peer", label: "+ Peers" },
                        ].filter((btn) => eligible.some((e) => e.relation === btn.key)).map((btn) => (
                          <button
                            key={btn.key}
                            type="button"
                            data-testid={`f360-quick-${btn.key}`}
                            onClick={() => {
                              const ids = eligible.filter((e) => e.relation === btn.key).map((e) => e.id);
                              setReviewers((cur) => Array.from(new Set([...cur, ...ids])));
                            }}
                            className="px-2 py-0.5 text-[11px] font-semibold rounded-sm border border-zenith-teal/40 bg-white text-zenith-teal-700 hover:bg-zenith-teal/10"
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {eligible.length === 0 && !suggestBusy && (
                      <div className="text-[11px] text-zinc-500">No suggestions — pick reviewers manually below.</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {eligible.map((r) => {
                        const relColor = {
                          reporting_manager: "border-adb-navy/40 text-adb-navy bg-adb-navy/5",
                          dotted_manager: "border-zenith-magenta/40 text-zenith-magenta bg-zenith-magenta/5",
                          direct_report: "border-zenith-teal/40 text-zenith-teal-700 bg-zenith-teal/5",
                          peer: "border-zinc-300 text-zinc-600 bg-zinc-50",
                        }[r.relation] || "border-zinc-300";
                        return (
                          <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-white cursor-pointer" data-testid={`f360-suggest-${r.id}`}>
                            <input type="checkbox" checked={reviewers.includes(r.id)} onChange={() => toggleReviewer(r.id)} />
                            <span className="text-xs flex-1">{r.name}</span>
                            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${relColor}`}>
                              {r.relation.replace(/_/g, "-")}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs mb-1 block">Selected reviewers ({reviewers.length})</Label>
                  <div className="border border-zinc-200 rounded-sm max-h-[24vh] overflow-y-auto">
                    {employees.filter((e) => e.id !== subj).map((e) => (
                      <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer">
                        <input type="checkbox" checked={reviewers.includes(e.id)} onChange={() => toggleReviewer(e.id)} data-testid={`f360-reviewer-${e.id}`} />
                        <span className="text-sm">{e.first_name} {e.last_name}</span>
                        <span className="text-xs font-mono-plex text-zinc-500">{e.employee_code}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={request} disabled={!subj || reviewers.length === 0} className="btn-zenith rounded-sm" data-testid={F360.saveBtn}>Send</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {user?.employee_id && (
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Feedback assigned to me</div>
          <div className="border border-zinc-200 bg-white rounded-sm" data-testid={F360.reviewerList}>
            {mine.as_reviewer.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">Nothing pending.</div>}
            {mine.as_reviewer.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-4 py-2 border-t border-zinc-100 first:border-t-0">
                <div>
                  <div className="text-sm">Subject: <span className="font-mono-plex">{r.subject_employee_id.slice(-6)}</span></div>
                  <div className="text-xs text-zinc-500">{r.status}</div>
                </div>
                {r.status === "pending" ? (
                  <Button size="sm" className="rounded-sm btn-zenith h-7" onClick={() => setSubmitOpen(r.id)} data-testid={F360.submitBtn(r.id)}>Give feedback</Button>
                ) : (
                  <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Submitted</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && (
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">View aggregated feedback</div>
          <Select value={subjectView} onValueChange={viewSubject}>
            <SelectTrigger className="w-[300px] rounded-sm"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
          </Select>
          {subjectView && (
            <div className="mt-3 space-y-2">
              {subjectFeedback.length === 0 && <div className="border border-zinc-200 bg-white p-6 rounded-sm text-center text-sm text-zinc-500">No submitted feedback yet.</div>}
              {subjectFeedback.map((s) => (
                <div key={s.id} className="border border-zinc-200 bg-white p-4 rounded-sm">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Overall {s.rating_overall}/5</Badge>
                    <div className="text-xs text-zinc-500 font-mono-plex">Reviewer #{s.reviewer_employee_id.slice(-6)}</div>
                  </div>
                  <div className="mt-2 text-sm"><span className="font-semibold text-emerald-800">Strengths:</span> {s.strengths}</div>
                  <div className="mt-1 text-sm"><span className="font-semibold text-amber-800">Improvements:</span> {s.improvements}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(s.ratings_dimensions || {}).map(([k, v]) => <Badge key={k} variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200 text-xs capitalize">{k}: {v}/5</Badge>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!submitOpen} onOpenChange={(v) => !v && setSubmitOpen(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Give 360° feedback</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Strengths *</Label><Textarea value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} className="rounded-sm" data-testid="f360-strengths" /></div>
            <div className="space-y-1"><Label className="text-xs">Improvement areas *</Label><Textarea value={f.improvements} onChange={(e) => setF({ ...f, improvements: e.target.value })} className="rounded-sm" data-testid="f360-improvements" /></div>
            <RatingLine label="Overall" value={f.rating_overall} onChange={(v) => setF({ ...f, rating_overall: v })} testid="f360-overall" />
            {Object.keys(f.ratings_dimensions).map((k) => (
              <RatingLine key={k} label={k} value={f.ratings_dimensions[k]} onChange={(v) => setF({ ...f, ratings_dimensions: { ...f.ratings_dimensions, [k]: v } })} testid={`f360-dim-${k}`} />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} className="btn-zenith rounded-sm" data-testid="f360-submit-form">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatingLine({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <Label className="text-xs capitalize w-28">{label}</Label>
      <div className="flex" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => onChange(n)} className="p-0.5">
            <Star size={18} weight={n <= value ? "fill" : "regular"} className={n <= value ? "text-emerald-700" : "text-zinc-300"} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------- 9-Box ----------------
function NineBoxPanel({ cycleId, isAdmin, params }) {
  const [grid, setGrid] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", performance: 2, potential: 2, notes: "" });

  const load = () => {
    api.get("/nine-box", { params: { ...params, cycle_id: cycleId } }).then(({ data }) => setGrid(data)).catch(() => setGrid([]));
    if (isAdmin) api.get("/employees", { params }).then(({ data }) => setEmployees(data)).catch(() => setEmployees([]));
  };
  useEffect(() => { if (cycleId) load(); /* eslint-disable-next-line */ }, [cycleId]);

  const save = async () => {
    try {
      const payload = { ...form, cycle_id: cycleId, performance: Number(form.performance), potential: Number(form.potential) };
      if (params.tenant_id) payload.tenant_id = params.tenant_id;
      await api.post("/nine-box", payload);
      toast.success("Placement saved");
      setOpen(false);
      setForm({ employee_id: "", performance: 2, potential: 2, notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cell = (perf, pot) => grid.filter((g) => g.performance === perf && g.potential === pot);

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid="ninebox-plot-btn"><Plus size={16} className="mr-1.5" /> Plot employee</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Place on 9-box</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="ninebox-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Performance</Label>
                  <Select value={String(form.performance)} onValueChange={(v) => setForm({ ...form, performance: Number(v) })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n} — {NUM_LABEL[n]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Potential</Label>
                  <Select value={String(form.potential)} onValueChange={(v) => setForm({ ...form, potential: Number(v) })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n} — {NUM_LABEL[n]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={save} className="btn-zenith rounded-sm" data-testid="ninebox-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-1">
        <div />
        <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 py-1">Low potential</div>
        <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 py-1">Mid potential</div>
        <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 py-1">High potential</div>

        {[3, 2, 1].map((perf) => (
          <RowNineBox key={perf} perf={perf} cell={cell} />
        ))}
      </div>
      <div className="text-[10px] text-zinc-500 mt-1">← performance axis: bottom rows = low, top rows = high · right columns = high potential</div>
    </div>
  );
}

function RowNineBox({ perf, cell }) {
  return (
    <>
      <div className="grid place-items-center text-[10px] uppercase tracking-widest text-zinc-500">{NUM_LABEL[perf]}<br />perf</div>
      {[1, 2, 3].map((pot) => {
        const box = LABELS_9BOX[`${perf}-${pot}`];
        const cells = cell(perf, pot);
        return (
          <div key={pot} data-testid={NB.cell(perf, pot)} className={`border-2 rounded-sm p-3 min-h-[140px] ${box.color}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold">{box.title}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {cells.map((c) => (
                <Badge key={c.id} variant="outline" className="rounded-sm bg-white/80 border-zinc-300 text-[10px]">{c.employee_name || c.employee_code}</Badge>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}
